import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getMatches,
  getOpenMatches,
  getOpenMatchesPaged,
  getMatchesSummary,
  getMatchById,
  getUserMatches,
  getUserMatchesPaged,
  getUserMatchesSummary,
  getOfficialMatches,
  createFromOfficial,
  refreshMatchLineup,
  restartMatchLineup,
  resetMatchToInitialLineup,
  updateManualMatchLineup,
  deleteMatch,
  deleteUserMatch,
  deleteUserMatchCascade,
  restoreUserMatch,
  confirmMatch,
  clearHeatResults,
  putHeatResults,
  updateHeatRiders,
  saveHeat,
  resolveUserMatch,
  getOfficialMatchById,
  getUserMatchById,
} from "../api/matches";
import { toast } from "sonner";
import { toDate, isSameSvDay, getSvYear, STOCKHOLM_TZ } from "@/utils/date";

const ACCOUNT_QUERY_KEY = ["account"];

async function refreshAccountStats(queryClient) {
  await queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEY });
  await queryClient.refetchQueries({ queryKey: ACCOUNT_QUERY_KEY, type: "all" });
}


// Centrala query keys för konsekvens
const qk = {
  matches: ["matches"],
  openMatches: ["matches", "open"],
  matchesSummary: (recentLimit = 10) => ["matchesSummary", recentLimit],
  match: (id) => ["match", id],
  userMatches: ["userMatches"],
  userMatchesSummary: (recentLimit = 5) => ["userMatchesSummary", recentLimit],
  officialMatches: (includeUsed = false) => ["officialMatches", includeUsed ? "includeUsed" : "default"],
  userMatch: (id) => ["userMatch", id],
  officialMatch: (id) => ["officialMatch", id],
};

// --- Läsfrågor (queries) ---

export function useMatches(options = {}) {
  return useQuery({
    queryKey: qk.matches,
    queryFn: getMatches,
    ...options, // låt komponenter override:a staleTime osv vid behov
  });
}

export function useOpenMatches(options = {}) {
  return useQuery({
    queryKey: qk.openMatches,
    queryFn: getOpenMatches,
    ...options,
  });
}

export function useOpenMatchesPaged(params, options = {}) {
  return useQuery({
    queryKey: [...qk.openMatches, "paged", params],
    queryFn: () => getOpenMatchesPaged(params),
    ...options,
  });
}

export function useMatchesSummary(options = {}) {
  const { recentLimit = 10, ...queryOptions } = options;
  return useQuery({
    queryKey: qk.matchesSummary(recentLimit),
    queryFn: () => getMatchesSummary(recentLimit),
    ...queryOptions,
  });
}

export function useMatch(id, options = {}) {
  return useQuery({
    queryKey: qk.match(id),
    queryFn: () => getMatchById(id),
    enabled: !!id,
    ...options,
  });
}

export function useUserMatches(options = {}) {
  return useQuery({
    queryKey: qk.userMatches,
    queryFn: getUserMatches,
    ...options,
  });
}

export function useUserMatchesSummary(options = {}) {
  const { recentLimit = 5, ...queryOptions } = options;
  return useQuery({
    queryKey: qk.userMatchesSummary(recentLimit),
    queryFn: () => getUserMatchesSummary(recentLimit),
    ...queryOptions,
  });
}

export function useUserMatch(userMatchId, options = {}) {
  return useQuery({
    queryKey: qk.userMatch(userMatchId),
    queryFn: () => getUserMatchById(userMatchId),
    enabled: !!userMatchId,
    ...options,
  });
}

export function useOfficialMatches(options = {}) {
  const { includeUsed = false, ...queryOptions } = options;
  return useQuery({
    queryKey: qk.officialMatches(includeUsed),
    queryFn: () => getOfficialMatches({ includeUsed }),
    ...queryOptions,
  });
}


export function useOfficialMatch(officialId, options = {}) {
  return useQuery({
    queryKey: qk.officialMatch(officialId),
    queryFn: () => getOfficialMatchById(officialId),
    enabled: !!officialId,
    ...options,
  });
}

// --- Mutationer (skriv) ---

// Skapa match från officiell
export function useCreateFromOfficial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (official_match_id) => createFromOfficial(official_match_id),
    onSuccess: async (_data, official_match_id) => {
      // Markera matchen som använd i cachen för direkt feedback
      if (official_match_id) {
        const officialIdStr = String(official_match_id);
        qc.setQueryData(qk.officialMatches(false), (previous) => {
          if (!Array.isArray(previous)) return previous;
          return previous.map((match) =>
            String(match?.id ?? match?._id ?? "") === officialIdStr
              ? { ...match, consumedByCurrentUser: true }
              : match
          );
        });
        qc.setQueryData(qk.officialMatch(official_match_id), (previous) =>
          previous ? { ...previous, consumedByCurrentUser: true } : previous
        );
      }
      // Hämta om listor som påverkas
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.matches }),
        qc.invalidateQueries({ queryKey: qk.userMatches }),
        qc.invalidateQueries({ queryKey: ["officialMatches"] }),
        qc.invalidateQueries({ queryKey: ["matchesSummary"] }),
        qc.invalidateQueries({ queryKey: ["userMatchesSummary"] }),
        refreshAccountStats(qc),
      ]);
      // om din UI visar official list för att skapa – fräscha den också
    },
  });
}

// Bekräfta match
export function useConfirmMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => {
      if (typeof input === "string") return confirmMatch(input);
      return confirmMatch(input?.id, input?.payload || {});
    },
    onSuccess: async (_data, input) => {
      const id = typeof input === "string" ? input : input?.id;
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.match(id) }),
        qc.invalidateQueries({ queryKey: qk.matches }),
        qc.invalidateQueries({ queryKey: ["matchesSummary"] }),
        qc.invalidateQueries({ queryKey: qk.userMatches }),
        qc.invalidateQueries({ queryKey: ["userMatchesSummary"] }),
        qc.refetchQueries({ queryKey: qk.userMatches, type: "all" }),
        qc.refetchQueries({ queryKey: ["userMatchesSummary"], type: "all" }),
        refreshAccountStats(qc),
      ]);
    },
  });
}

export function useUserMatchesPaged(params, options = {}) {
  return useQuery({
    queryKey: [...qk.userMatches, "paged", params],
    queryFn: () => getUserMatchesPaged(params),
    ...options,
  });
}

// Radera match
export function useDeleteMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteMatch(id),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.matches }),
        qc.invalidateQueries({ queryKey: qk.userMatches }),
        qc.invalidateQueries({ queryKey: ["officialMatches"] }),
        qc.invalidateQueries({ queryKey: ["matchesSummary"] }),
        qc.invalidateQueries({ queryKey: ["userMatchesSummary"] }),
        refreshAccountStats(qc),
      ]);
    },
  });
}

// Radera user-match (vanlig)
export function useDeleteUserMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userMatchId) => deleteUserMatch(userMatchId),
    onSuccess: async (_data, userMatchId) => {
      const invalidations = [
        qc.invalidateQueries({ queryKey: qk.userMatches }),
        qc.invalidateQueries({ queryKey: ["userMatchesSummary"] }),
        refreshAccountStats(qc),
      ];
      if (userMatchId) invalidations.push(qc.invalidateQueries({ queryKey: qk.userMatch(userMatchId) }));
      await Promise.all(invalidations);
    },
  });
}

// Radera user-match (cascade)
export function useDeleteUserMatchCascade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userMatchId) => deleteUserMatchCascade(userMatchId),
    onSuccess: async (_data, userMatchId) => {
      const invalidations = [
        qc.invalidateQueries({ queryKey: qk.userMatches }),
        qc.invalidateQueries({ queryKey: qk.matches }),
        qc.invalidateQueries({ queryKey: ["officialMatches"] }),
        qc.invalidateQueries({ queryKey: ["userMatchesSummary"] }),
        qc.invalidateQueries({ queryKey: ["matchesSummary"] }),
        refreshAccountStats(qc),
        // Viktigt: om samma protokoll är öppet i shellen måste detalj-cachen också invalideras.
        qc.invalidateQueries({ queryKey: ["match"] }),
      ];
      if (userMatchId) invalidations.push(qc.invalidateQueries({ queryKey: qk.userMatch(userMatchId) }));
      await Promise.all(invalidations);
    },
  });
}

// Lösa user-match (approve/decline etc.)
export function useResolveUserMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userMatchId, action }) => resolveUserMatch(userMatchId, action),
    onSuccess: async (_data, { userMatchId }) => {
      const invalidations = [
        qc.invalidateQueries({ queryKey: qk.userMatches }),
        qc.invalidateQueries({ queryKey: ["userMatchesSummary"] }),
      ];
      if (userMatchId) invalidations.push(qc.invalidateQueries({ queryKey: qk.userMatch(userMatchId) }));
      await Promise.all(invalidations);
    },
  });
}

// Heat-resultat
export function useClearHeatResults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, heatNumber }) => clearHeatResults(matchId, heatNumber),
    onSuccess: async (_data, { matchId }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.match(matchId) }),
        qc.invalidateQueries({ queryKey: qk.matches }),
        qc.invalidateQueries({ queryKey: qk.userMatches }),
      ]);
    },
  });
}

export function usePutHeatResults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, heatNumber, results }) =>
      putHeatResults(matchId, heatNumber, results),
    onSuccess: async (_data, { matchId }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.match(matchId) }),
        qc.invalidateQueries({ queryKey: qk.matches }),
        qc.invalidateQueries({ queryKey: qk.userMatches }),
      ]);
    },
  });
}

// export function useUpdateHeatRiders() {
//   const qc = useQueryClient();
//   return useMutation({
//     mutationFn: ({ matchId, heatNumber, assignments }) =>
//       updateHeatRiders(matchId, heatNumber, assignments),
//     onSuccess: (_data, { matchId }) => {
//       qc.invalidateQueries({ queryKey: qk.match(matchId) });
//       qc.invalidateQueries({ queryKey: qk.matches });
//       qc.invalidateQueries({ queryKey: qk.userMatches });
//     },
//   });
// }

export function useUpdateHeatRiders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, heatNumber, assignments, rr = false }) =>
      updateHeatRiders(matchId, heatNumber, assignments, { rr }),
    onSuccess: async (_data, { matchId }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.match(matchId) }),
        qc.invalidateQueries({ queryKey: qk.matches }),
        qc.invalidateQueries({ queryKey: qk.userMatches }),
      ]);
    },
    onError: (e) => {
      // Visa ett trevligt fel—men låt mutationen fortsatt rejecta
      toast.error(e?.message || "Kunde inte uppdatera heat");
    },
    retry: false,
  });
}

export function useResetMatchToInitialLineup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, team }) => resetMatchToInitialLineup(matchId, team),
    onSuccess: async (_data, { matchId }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.match(matchId) }),
        qc.invalidateQueries({ queryKey: qk.matches }),
        qc.invalidateQueries({ queryKey: qk.userMatches }),
      ]);
    },
  });
}

export function useRestoreUserMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userMatchId) => restoreUserMatch(userMatchId),
    onSuccess: async (data, userMatchId) => {
      const matchId = data?.match_id;
      const invalidations = [
        qc.invalidateQueries({ queryKey: qk.userMatches }),
        qc.invalidateQueries({ queryKey: qk.matches }),
        qc.invalidateQueries({ queryKey: ["userMatchesSummary"] }),
        qc.invalidateQueries({ queryKey: ["matchesSummary"] }),
        refreshAccountStats(qc),
        qc.invalidateQueries({ queryKey: ["match"] }),
        qc.refetchQueries({ queryKey: qk.userMatches, type: "all" }),
        qc.refetchQueries({ queryKey: qk.matches, type: "all" }),
      ];
      if (userMatchId) invalidations.push(qc.invalidateQueries({ queryKey: qk.userMatch(userMatchId) }));
      if (matchId) invalidations.push(qc.invalidateQueries({ queryKey: qk.match(matchId) }));
      await Promise.all(invalidations);
    },
  });
}

export function useSaveHeat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, heatNumber, assignments, results, rr = false, heatTime = undefined }) => {
      const payload = {};
      if (assignments && Object.keys(assignments).length > 0) {
        payload.assignments = assignments;
      }
      if (Array.isArray(results)) {
        payload.results = results;
      }
      if (heatTime !== undefined) {
        payload.heat_time = heatTime;
      }
      if (rr) {
        payload.rr = true;
      }
      return saveHeat(matchId, heatNumber, payload);
    },
    onSuccess: (data, { matchId, heatNumber }) => {
      const matchIdStr = String(matchId);
      const mergeHeat = (previousHeats = []) => {
        if (!Array.isArray(previousHeats)) return previousHeats;
        return previousHeats.map((heat) =>
          heat?.heat_number === heatNumber
            ? {
              ...heat,
              ...data.heat,
              riders: data?.heat?.riders ?? heat?.riders,
              results: data?.heat?.results ?? heat?.results,
            }
            : heat
        );
      };
      qc.setQueryData(qk.match(matchId), (previous) => {
        if (!previous) return previous;
        const heats = mergeHeat(previous.heats);
        return {
          ...previous,
          heats,
          home_score: data.home_score ?? previous.home_score,
          away_score: data.away_score ?? previous.away_score,
        };
      });

      qc.setQueryData(qk.matches, (previous) => {
        if (!Array.isArray(previous)) return previous;
        return previous.map((match) => {
          if (String(match?.id ?? match?._id ?? "") !== matchIdStr) {
            return match;
          }

          const heats = mergeHeat(match?.heats);
          return {
            ...match,
            heats,
            home_score: data.home_score ?? match?.home_score,
            away_score: data.away_score ?? match?.away_score,
          };
        });
      });

      qc.setQueryData(qk.userMatches, (previous) => {
        if (!Array.isArray(previous)) return previous;
        return previous.map((match) => {
          const idsToCompare = [
            match?.match_id,
            match?.id,
            match?._id,
            match?.match?.id,
          ]
            .filter(Boolean)
            .map(String);

          if (!idsToCompare.includes(matchIdStr)) {
            return match;
          }

          const updated = {
            ...match,
            home_score: data.home_score ?? match?.home_score,
            away_score: data.away_score ?? match?.away_score,
          };

          if (Array.isArray(match?.heats)) {
            updated.heats = mergeHeat(match.heats);
          }

          if (match?.match && Array.isArray(match.match.heats)) {
            updated.match = {
              ...match.match,
              heats: mergeHeat(match.match.heats),
            };
          }

          return updated;
        });
      });
    },
    onError: (e) => {
      const message = e?.message || e?.detail || "Kunde inte spara heatet";
      toast.error(message);
    },
    retry: false,
  });
}



// --- Selectors (rena funktioner) ---
function isBeforeStockholmCutoff(now, hour = 21, minute = 0) {
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: STOCKHOLM_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const currentHour = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const currentMinute = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  return currentHour < hour || (currentHour === hour && currentMinute < minute);
}

export const selectUpcomingByDate = (now = new Date()) => (arr = []) =>
  arr
    .filter((m) => {
      const d = toDate(m.date);
      if (!d || Number.isNaN(d.getTime())) return false;
      if (d > now) return true;
      return isSameSvDay(d, now) && isBeforeStockholmCutoff(now, 21, 0);
    })
    .sort((a, b) => {
      const da = toDate(a.date);
      const db = toDate(b.date);
      return (da?.getTime() || 0) - (db?.getTime() || 0);
    });

export const selectSameDayBundle = (arr = []) => {
  const upcoming = selectUpcomingByDate()(arr);
  const nextOfficial = upcoming[0] || null;
  if (!nextOfficial) return { upcoming, nextOfficial: null, sameDayAll: [] };

  const sameDay = upcoming
    .filter((m) => isSameSvDay(m.date, nextOfficial.date))
    .sort((a, b) => {
      const da = toDate(a.date);
      const db = toDate(b.date);
      return (da?.getTime() || 0) - (db?.getTime() || 0);
    });

  const heroId = nextOfficial.id || nextOfficial._id;
  const rest = sameDay.filter((m) => (m.id || m._id) !== heroId);
  return { upcoming, nextOfficial, sameDayAll: [nextOfficial, ...rest] };
};

// H2H (innevarande säsong)
export function computeH2HInSeason(matches = [], teamA, teamB, year) {
  if (!teamA || !teamB || !year) return null;
  const relevant = matches.filter((m) => {
    const d = toDate(m.date);
    const homeIdentity = m.home_team_id || m.home_team_name_canonical || m.home_team;
    const awayIdentity = m.away_team_id || m.away_team_name_canonical || m.away_team;
    return (
      d && !Number.isNaN(d.getTime()) &&
      getSvYear(d) === year &&
      [homeIdentity, awayIdentity].includes(teamA) &&
      [homeIdentity, awayIdentity].includes(teamB) &&
      m.home_score != null &&
      m.away_score != null
    );
  });
  let aWins = 0, bWins = 0;
  for (const m of relevant) {
    if (m.home_score > m.away_score) {
      if ((m.home_team_id || m.home_team_name_canonical || m.home_team) === teamA) aWins++; else bWins++;
    } else if (m.away_score > m.home_score) {
      if ((m.away_team_id || m.away_team_name_canonical || m.away_team) === teamA) aWins++; else bWins++;
    }
  }
  return `${aWins} – ${bWins}`;
}

// --- Härledda hooks ---
// 1) Bara kommande officiella
export function useOfficialUpcoming(options = {}) {
  return useOfficialMatches({
    select: selectUpcomingByDate(),
    ...options,
  });
}

// 2) Paket: upcoming + nextOfficial + sameDayAll
export function useOfficialSameDayBundle(options = {}) {
  return useOfficialMatches({
    select: selectSameDayBundle,
    ...options,
  });
}

export function useRefreshMatchLineup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matchId) => refreshMatchLineup(matchId),
    onSuccess: async (_data, matchId) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.match(matchId) }),
        qc.invalidateQueries({ queryKey: qk.matches }),
        qc.invalidateQueries({ queryKey: qk.userMatches }),
        qc.invalidateQueries({ queryKey: ["matchesSummary"] }),
        qc.invalidateQueries({ queryKey: ["userMatchesSummary"] }),
      ]);
    },
  });
}

export function useRestartMatchLineup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matchId) => restartMatchLineup(matchId),
    onSuccess: async (_data, matchId) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.match(matchId) }),
        qc.invalidateQueries({ queryKey: qk.matches }),
        qc.invalidateQueries({ queryKey: qk.userMatches }),
        qc.invalidateQueries({ queryKey: ["matchesSummary"] }),
        qc.invalidateQueries({ queryKey: ["userMatchesSummary"] }),
      ]);
    },
  });
}

export function useUpdateManualMatchLineup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, lineups }) => updateManualMatchLineup(matchId, lineups),
    onSuccess: async (_data, { matchId }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.match(matchId) }),
        qc.invalidateQueries({ queryKey: qk.matches } ),
        qc.invalidateQueries({ queryKey: qk.userMatches }),
        qc.invalidateQueries({ queryKey: ["matchesSummary"] }),
        qc.invalidateQueries({ queryKey: ["userMatchesSummary"] }),
      ]);
    },
    onError: (e) => {
      toast.error(e?.message || "Kunde inte uppdatera laguppställningen");
    },
    retry: false,
  });
}
